import { ref } from 'vue'
import { getMyTasks } from '@/api/flow'
import { useGlobalToastCenter } from '@/composables/useGlobalToastCenter'

const approvalList = ref([])
const seenApprovalIds = new Set()
const approvalByTaskId = new Map()
const taskIdsByInstanceId = new Map()
const { pushToast, removeToastBySourceId, removeToastsBySource } = useGlobalToastCenter()
let pollTimer = null
let pollGeneration = 0
let isFetching = false
const POLL_INTERVAL = 30000
const MAX_QUEUE = 30

function pickList(data) {
  if (Array.isArray(data)) return data
  return data?.records || data?.list || data?.rows || data?.data?.records || data?.data?.list || []
}

function normalizeApprovalTask(task) {
  const id = task?.id || task?.taskId || task?.approvalTaskId || ''
  const instanceId = task?.instanceId || task?.processInstanceId || task?.processInstanceBusinessKey || ''
  const applicantName = task?.applicantName || task?.starterName || task?.initiatorName || task?.creatorName || '未知'
  const type = task?.type || task?.taskName || task?.templateName || task?.processName || task?.businessType || '审批'
  const title = task?.title || task?.taskTitle || task?.approvalTitle || task?.processTitle || '待审批任务'

  return {
    id,
    instanceId,
    title,
    type,
    applicantName,
    applicantRole: task?.applicantRole || task?.starterRole || task?.initiatorRole || '',
    businessType: task?.businessType || task?.bizType || '',
    businessId: task?.businessId || task?.bizId || task?.formId || '',
    createTime: task?.createTime || task?.createdAt || task?.startTime || task?.applyTime || '',
    description: task?.description || task?.remark || task?.summary || ''
  }
}

function getApprovalKey(id) {
  if (id === undefined || id === null || id === '') return ''
  return String(id)
}

function rememberApproval(item) {
  const taskId = getApprovalKey(item?.id)
  if (!taskId) return

  const previousItem = approvalByTaskId.get(taskId)
  const previousInstanceId = getApprovalKey(previousItem?.instanceId)
  approvalByTaskId.set(taskId, item)

  const instanceId = getApprovalKey(item?.instanceId)
  if (previousInstanceId && previousInstanceId !== instanceId) {
    const previousTaskIds = taskIdsByInstanceId.get(previousInstanceId)
    if (previousTaskIds) {
      previousTaskIds.delete(taskId)
      if (!previousTaskIds.size) taskIdsByInstanceId.delete(previousInstanceId)
    }
  }

  if (!instanceId) return

  if (!taskIdsByInstanceId.has(instanceId)) {
    taskIdsByInstanceId.set(instanceId, new Set())
  }
  taskIdsByInstanceId.get(instanceId).add(taskId)
}

function findApprovalById(id) {
  const key = getApprovalKey(id)
  if (!key) return null

  const taskItem = approvalByTaskId.get(key)
  if (taskItem) return taskItem

  const taskIds = taskIdsByInstanceId.get(key)
  if (!taskIds || taskIds.size !== 1) return null

  const [taskId] = taskIds
  return approvalByTaskId.get(taskId) || approvalList.value.find(item => String(item.id) === taskId) || null
}

function hasSeenApproval(item) {
  const taskId = getApprovalKey(item?.id)
  return taskId ? seenApprovalIds.has(taskId) : false
}

function markSeenApproval(item) {
  const taskId = getApprovalKey(item?.id)
  if (taskId) seenApprovalIds.add(taskId)
}

function deleteSeenApproval(itemOrId) {
  if (typeof itemOrId === 'object') {
    const taskId = getApprovalKey(itemOrId?.id)
    if (taskId) seenApprovalIds.delete(taskId)
    return
  }
  const key = getApprovalKey(itemOrId)
  if (key) seenApprovalIds.delete(key)
}

function deleteMappedApproval(itemOrId) {
  const item = typeof itemOrId === 'object' ? itemOrId : findApprovalById(itemOrId)
  if (item) {
    const taskId = getApprovalKey(item.id)
    if (taskId) approvalByTaskId.delete(taskId)

    const instanceId = getApprovalKey(item.instanceId)
    const taskIds = taskIdsByInstanceId.get(instanceId)
    if (taskIds && taskId) {
      taskIds.delete(taskId)
      if (!taskIds.size) taskIdsByInstanceId.delete(instanceId)
    }
    return
  }

  const key = getApprovalKey(itemOrId)
  if (key) approvalByTaskId.delete(key)
}

function matchesApprovalId(item, id) {
  return String(item?.id) === getApprovalKey(id)
}

function isAmbiguousInstanceId(id) {
  const key = getApprovalKey(id)
  const taskIds = taskIdsByInstanceId.get(key)
  return !!taskIds && taskIds.size !== 1 && !approvalByTaskId.has(key)
}

function removeApprovalToast(id) {
  if (id === undefined || id === null || id === '') return
  removeToastBySourceId('approval', id)
}

function pushApprovalToast(normalized) {
  if (!normalized.id) return
  pushToast({
    id: `approval:${normalized.id}`,
    source: 'approval',
    rawId: normalized.id,
    title: normalized.title,
    content: normalized.description || normalized.type,
    meta: normalized.applicantRole ? `${normalized.applicantName}（${normalized.applicantRole}）` : normalized.applicantName,
    time: normalized.createTime || '',
    sortTime: normalized.createTime || '',
    variant: 'approval',
    actions: ['process'],
    payload: normalized
  })
}

function startPolling() {
  stopPolling()
  const gen = ++pollGeneration
  pollTimer = setInterval(async () => {
    try {
      await fetchAndDiff(gen)
    } catch (e) {
      console.error('[useApprovalToast] poll failed', e)
    }
  }, POLL_INTERVAL)
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
  pollGeneration++
}

function removeApprovalItem(itemOrId) {
  const item = typeof itemOrId === 'object' ? itemOrId : findApprovalById(itemOrId)

  if (!item) {
    if (isAmbiguousInstanceId(itemOrId)) return
    deleteSeenApproval(itemOrId)
    deleteMappedApproval(itemOrId)
    removeApprovalToast(itemOrId)
    return
  }

  removeApprovalToast(item.id)
  deleteSeenApproval(item)
  deleteMappedApproval(item)
}

async function fetchAndDiff(gen = pollGeneration) {
  if (isFetching) return
  isFetching = true

  try {
    const data = await getMyTasks({
      pageNum: 1,
      pageSize: MAX_QUEUE,
      status: 'PENDING'
    })
    if (gen !== pollGeneration) return

    const normalized = pickList(data)
      .map(normalizeApprovalTask)
      .filter(item => item.id)
    const returnedTaskIds = new Set(normalized.map(item => getApprovalKey(item.id)).filter(Boolean))

    normalized.forEach(rememberApproval)
    approvalList.value = normalized.slice(0, 5)

    Array.from(approvalByTaskId.entries()).forEach(([taskId, item]) => {
      if (!returnedTaskIds.has(taskId)) {
        removeApprovalItem(item)
      }
    })

    normalized.forEach(item => {
      if (hasSeenApproval(item)) return
      markSeenApproval(item)
      pushApprovalToast(item)
    })
  } catch (e) {
    console.error('[useApprovalToast] load failed', e)
  } finally {
    isFetching = false
  }
}

async function init() {
  startPolling()
  await fetchAndDiff(pollGeneration)
}

function reset() {
  stopPolling()
  removeToastsBySource('approval')
  approvalList.value = []
  seenApprovalIds.clear()
  approvalByTaskId.clear()
  taskIdsByInstanceId.clear()
}

function syncHandledApproval(id) {
  const matchedItem = findApprovalById(id)
  if (matchedItem) {
    approvalList.value = approvalList.value.filter(item => !matchesApprovalId(item, matchedItem.id))
  } else {
    const directTaskId = getApprovalKey(id)
    approvalList.value = approvalList.value.filter(item => !matchesApprovalId(item, directTaskId))
  }

  removeApprovalItem(matchedItem || id)
}

function dismissApprovalToast(id) {
  const matchedItem = findApprovalById(id)
  if (matchedItem) {
    removeApprovalToast(matchedItem.id)
    return
  }
  if (isAmbiguousInstanceId(id)) return
  removeApprovalToast(id)
}

export function useApprovalToast() {
  return {
    approvalList,
    init,
    reset,
    fetchAndDiff,
    syncHandledApproval,
    dismissApprovalToast
  }
}
